import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Text,
  useTheme,
} from "@chakra-ui/react";
import { FadersHorizontal } from "@phosphor-icons/react";
import SearchString from "search-string";
import { prettyPrintCodeName, prettyPrintLowMedianHighStatuses } from "utils/text-utils";
import { useRightSidebar } from "context/RightSidebarContext";
import { useEffect, useMemo, useCallback } from "react";
import { startCase } from "lodash";
import GLOBAL from "context/global";
import useAirUserRoles from "utils/AuthUtils";

const CATEGORIES =
  "owner stage assignees questionGroup sectors regulation completeness confidence decisionUsefulness sensitivity".split(
    " "
  ); // Note: topic doesnt seem to be present in the data

/**
 * Design: https://www.figma.com/board/47Oms7MiOVhcVw7SxahOtN/FINTECH-WORKSHOP-DEMO?node-id=0-4325&t=4VN8cEJ7eSGUHh38-0
 *
 * NOte: This can be so slow that it appears buggy!
 * That is probably due to slow rendering in the DataInputPage.
 *
 * @param {string} filterBy
 * @param {function} setFilterBy
 * @param {Question[]} fullList This lets us find the attributes that are available to filter by
 * @param filteredList
 * @returns
 */
const FilterByComponent = ({ filterBy, setFilterBy, fullList, filteredList }) => {
  const isActive = !!filterBy;
  const conditions = useMemo(() => (filterBy ? SearchString.parse(filterBy).getConditionArray() : []), [filterBy]);
  const filterKeys = filterBy ? [...new Set(conditions.map((c) => c.keyword))] : [];
  let questionIdsFilterLabel, questionIdsFilterIsActive;
  if (filterKeys.includes("questionIdsFilter") && GLOBAL.questionIdsFilter) {
    questionIdsFilterLabel = GLOBAL.questionIdsFilter.label;
    const qids = GLOBAL.questionIdsFilter.ids;
    questionIdsFilterIsActive = !!conditions.find((c) => c.keyword === "questionIdsFilter" && c.value === "true");
    // if active, reduce the fullList to only include the qids -- so that the other filter options offered are non-empty choices
    if (questionIdsFilterIsActive) {
      fullList = fullList.filter((q) => qids.includes(q.id));
    }
  }

  const { colors } = useTheme();

  const { setRightSidebar, doOpen, isOpen, activationId } = useRightSidebar();

  // Note: useCallback means this function only updates when the dependencies change -- which then means the useEffect below will only run when the dependencies change
  const updateRightSidebar = useCallback(() => {
    setRightSidebar(
      <Flex gap={4} direction="column">
        <Text fontSize="heading.m" color="gray.600" fontWeight="bold">
          Filters
        </Text>
        {filteredList && <Text>{filteredList.length} questions</Text>}

        {questionIdsFilterLabel && (
          <Checkbox
            color="gray.500"
            colorScheme="limeShade"
            isChecked={questionIdsFilterIsActive}
            onChange={(e) => {
              const modFilterBy = filterBy.replace(
                /questionIdsFilter:(true|false)/,
                e.target.checked ? "questionIdsFilter:true" : "questionIdsFilter:false"
              );
              setFilterBy(modFilterBy);
            }}
          >
            <Text>{questionIdsFilterLabel}</Text>
          </Checkbox>
        )}

        {/* the main form */}
        <FilterByForm filterBy={filterBy} setFilterBy={setFilterBy} conditions={conditions} fullList={fullList || []} />
      </Flex>
    ); /* .end setRightSidebar */
  }, [
    setRightSidebar,
    filteredList,
    filterBy,
    setFilterBy,
    conditions,
    fullList,
    questionIdsFilterLabel,
    questionIdsFilterIsActive,
  ]);

  useEffect(() => {
    if (isOpen && activationId === "FilterByComponent") {
      updateRightSidebar();
    }
  }, [updateRightSidebar, activationId, isOpen, filterBy]); // Re-renders sidebar when filterBy changes

  return (
    <Flex id="filter-by-component" gap={2}>
      <Button
        variant="primary"
        onClick={() => {
          updateRightSidebar();
          doOpen("FilterByComponent");
        }}
        color={isActive ? "pureWhite" : "pureBlack"}
        bg={isActive ? "teal" : "gray.100"}
        fontSize="body.xs"
        rightIcon={<FadersHorizontal color={colors.limeShade[600]} />}
        size="sm"
      >
        Filter by{isActive ? `: ${filterKeys.map(startCase).join(", ")}` : ""}
      </Button>
    </Flex>
  );
};

/**
 * HACK mark "plain text" search terms (because simple-search-string doesn't properly support it)
 */
export const TEXT_KEYWORD = "term";

/**
 * The accordion meat of the filter-by component.
 * @param {*} param0
 * @returns
 */
export function FilterByForm({ filterBy, setFilterBy, conditions, fullList }) {
  let categories = CATEGORIES;
  // relevance is confusing -- the overlap with excluded (where we'd like to refactor) + it can be unset
  // So only show to internal users for now -- mostly as a diagnostic tool
  const { isESGAdmin, isDeveloper } = useAirUserRoles();
  if (isESGAdmin || isDeveloper()) {
    categories = [...categories, "relevance"];
  }
  // open the categories that have conditions set
  let openCategories = [];
  for (let i = 0; i < categories.length; i++) {
    if (conditions.some((c) => c.keyword === categories[i])) {
      openCategories.push(i);
    }
  }

  // filter by text search against all-of-question
  let filterByText = conditions
    .filter((c) => c.keyword === TEXT_KEYWORD)
    .map((c) => c.value)
    .join(" ");
  const setFilterByText = (value) => {
    let ss = SearchString.parse(filterBy);
    // remove previous
    ss.removeKeyword(TEXT_KEYWORD);
    ss.getConditionArray().forEach((c) => {
      if (c.keyword === TEXT_KEYWORD) {
        ss.removeEntry(TEXT_KEYWORD, c.value, c.negated);
      }
    });
    // add new
    ss.addEntry(TEXT_KEYWORD, value, false);
    const sss = ss.toString();
    setFilterBy(sss);
  };

  return (
    <>
      <Input
        size="sm"
        borderRadius="md"
        placeholder="Filter by keyword"
        type="search"
        mb={4}
        value={filterByText}
        onChange={(e) => setFilterByText(e.target.value)}
      />
      <Accordion allowMultiple defaultIndex={openCategories}>
        {categories.map((category) => (
          <AccordionItem key={category} borderColor="gray.300">
            <AccordionButton>
              <Text flex="1" textAlign="left" color="gray.600" fontWeight="bold" fontSize="heading.xs" py={2}>
                {startCase(category)}
              </Text>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <FilterByFormCategory
                key={category}
                category={category}
                filterBy={filterBy}
                setFilterBy={setFilterBy}
                conditions={conditions}
                fullList={fullList}
              />
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </>
  );
}

function FilterByFormCategory({ category, filterBy, setFilterBy, conditions, fullList }) {
  // const t = i18n({ keyPrefix: category });
  // what are the in-use options for this category?
  let optionCounts = {};
  for (let item of fullList) {
    let value = item[category];
    if (value === undefined || value === null || value === "") continue;
    optionCounts[value] = (optionCounts[value] || 0) + 1;
  }
  const inUseOptions = Object.keys(optionCounts);

  const allOptions = [];

  if (category === "assignees") {
    inUseOptions.forEach((option) => {
      option?.split(",").forEach((o) => {
        if (!allOptions.includes(o)) {
          allOptions.push(o);
        }
      });
    });
  } else {
    allOptions.push(...inUseOptions);
  }

  // sort options by frequency?? alphabetically??
  // options = Object.entries(optionCounts).sort((a, b) => b[1] - a[1]).map(([value, count]) => value);
  // HACK minimum edit for question-groups

  // Note: Made some changes to the sorting logic to handle cases where there are numbers as well as no numbers in the string
  if (category === "questionGroup") {
    // Helper function to extract the numeric part from the option string
    const extractNumberFromString = (str) => {
      //Regext to find the first number in the string
      const m = str.match(/\b(\d+)\b/);
      return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER; // If no number found, I am setting it to max, to push the value to the end.
    }

    allOptions.sort((a, b) => {
      const numberInA = extractNumberFromString(a);
      const numberInB = extractNumberFromString(b);
      if(numberInA !== numberInB) {
        return numberInA - numberInB; // Sort by the extracted number
      }
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }); // If numbers are the same, sort alphabetically
    });
  }

  if (allOptions.length === 0) {
    return <Text fontSize="body.xs">No options found for {category}</Text>;
  }
  const toggleFilterBy = (category, option, e) => {
    let ss = SearchString.parse(filterBy);
    if (e.target.checked) {
      ss.addEntry(category, option, false);
    } else {
      ss.removeEntry(category, option, false);
    }
    setFilterBy(ss.toString());
  };

  return (
    <div>
      {allOptions.map((option) => (
        <Box key={option}>
          <Checkbox
            color="gray.500"
            colorScheme="limeShade"
            isChecked={conditions.some((c) => c.keyword === category && c.value === option)}
            onChange={(e) => toggleFilterBy(category, option, e)}
          >
            <Text as="span" fontSize="body.xs">
              {category === "completeness" ||
              category === "confidence" ||
              category === "decisionUsefulness" ||
              category === "sensitivity" ||
              category === "regulation"
                ? prettyPrintLowMedianHighStatuses(option, category)
                : prettyPrintCodeName(option)}
            </Text>
          </Checkbox>
        </Box>
      ))}
    </div>
  );
}

export default FilterByComponent;
